import Info from '@mui/icons-material/Info';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import useTheme from '@mui/material/styles/useTheme';
import { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import Iconify from '../../components/iconify/component';
import { type IconifyProps } from '../../components/iconify/types';
import { type CustomTheme } from '../../provider/ThemeProvider';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import InputNonEditValue from './helpers/render-non-edit-value';

type NumberFormatCustomProps = {
    inputRef?: () => void;
    name?: string;
    onChange?: (e: any) => void;
};

function NumberFormatCustom(props: NumberFormatCustomProps) {
    const { inputRef, name, onChange, ...other } = props;

    return (
        <NumericFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values: any) => {
                onChange({
                    target: {
                        name: name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator="."
            decimalSeparator=","
            allowNegative
            valueIsNumericString
            suffix={name || ''}
        />
    );
}

export type InputNumberProps = {
    autoWidth?: boolean;
    autocomplete?: string;
    blink?: boolean;
    ca?: boolean;
    callback?: (params: { id: string; val: number }) => void;
    className?: string;
    disabled?: boolean;
    edit?: boolean;
    einheit?: string;
    endIcon?: IconifyProps;
    error?: string;
    fatLabel?: boolean;
    helperText?: string;
    hideLabel?: boolean;
    hideLabelEdit?: boolean;
    icon?: IconifyProps;
    id?: string;
    info?: string;
    l?: string;
    loading?: boolean;
    max?: number;
    min?: number;
    onKeyUp?: () => void;
    testid?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    type?: 'decimal' | 'currency' | 'percent';
    unrounded?: number;
    upperCase?: boolean;
    v?: number;
    vNew?: number;
};

export function convertNumberValue(
    v: InputNumberProps['v'],
    type: InputNumberProps['type'],
    unrounded: InputNumberProps['unrounded'],
    einheit: InputNumberProps['einheit'],
    ca: InputNumberProps['ca']
): string {
    const vUse: number = typeof v === 'string' ? parseFloat(v) : v;
    const val: number = !checkUndefinedNullOrEmpty(vUse) && !isNaN(vUse) ? vUse : undefined;
    if (!checkUndefinedOrNull(val)) {
        const returnValue: string[] = [];
        returnValue.push(
            (val / (type === 'percent' ? 100 : 1)).toLocaleString('de-DE', { style: type, currency: 'EUR', minimumFractionDigits: unrounded || 0, maximumFractionDigits: unrounded || 0 })
        );
        if (ca) returnValue.unshift('ca.');
        if (einheit) returnValue.push(einheit);
        return returnValue.join(' ');
    }
    return '-';
}

const InputNumber = (moduleProps: InputNumberProps) => {
    const {
        autoWidth,
        autocomplete,
        blink,
        callback,
        ca,
        disabled,
        edit,
        einheit,
        endIcon,
        error,
        fatLabel,
        helperText,
        hideLabel,
        hideLabelEdit,
        icon,
        id,
        info,
        l,
        loading,
        max,
        min,
        onKeyUp,
        testid,
        textAlign,
        type,
        unrounded,
        upperCase,
        v,
        vNew,
    }: InputNumberProps = {
        autoWidth: false,
        blink: false,
        ca: false,
        disabled: false,
        edit: false,
        fatLabel: false,
        hideLabel: false,
        hideLabelEdit: false,
        loading: false,
        type: 'decimal',
        upperCase: false,
        ...moduleProps,
    };
    const [value, setValue] = useState<InputNumberProps['v']>(v);
    const [validate, setValidate] = useState<string>(undefined);
    const theme: CustomTheme = useTheme();

    const handleSubmit = (newVal: number | undefined) => {
        if (callback) callback({ id: id, val: newVal });
        else console.error('Callback-Funktion fehlt!\nInput-Key: inputNumber_' + id);
    };

    const handleChange = (e) => {
        const newValue = !checkUndefinedNullOrEmpty(e.target.value) ? parseFloat(e.target.value) : null;

        if (!checkUndefinedNullOrEmpty(min) && newValue < min) setValidate('Der Wert ist zu klein!');
        else if (!checkUndefinedNullOrEmpty(max) && newValue > max) setValidate('Der Wert ist zu groß!');
        else setValidate(undefined);

        setValue(newValue);
        handleSubmit(newValue);
    };

    const heading = [];
    if (!hideLabel) {
        if (icon) {
            heading.push(
                <span key={'inputNumber_' + id + '_headingIcon'} className="materialIcon iconMarginRight">
                    <Iconify icon={icon} />
                </span>
            );
        }
        if (l) heading.push(<span key={'inputNumber_' + id + '_heading'}>{l}</span>);
    }

    useEffect(
        function () {
            setValue(!checkUndefined(vNew) ? vNew : v);
        },
        [v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                setTimeout(() => {
                    const element = document.getElementById(id);
                    if (!checkUndefinedOrNull(element)) {
                        element.scrollIntoView({ block: 'center' });
                    }
                }, 500);
            }
        },
        [blink, id]
    );

    return edit ? (
        <div
            className={[blink ? 'blinkInputfield' : '', 'inputNumber'].join(' ')}
            key={'inputNumber_' + id}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: textAlign === 'right' ? 'flex-end' : textAlign === 'center' ? 'center' : textAlign === 'justify' ? 'space-between' : 'flex-start',
                width: !autoWidth ? '100%' : undefined,
                marginBottom: !checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText) ? undefined : 10,
            }}
        >
            {(loading || icon) && (
                <div
                    className="materialIcon iconMarginRight"
                    style={{ color: (!loading && !checkUndefinedOrNull(error)) || !checkUndefinedOrNull(validate) ? theme?.palette?.error?.main : undefined, marginTop: 20, marginBottom: -20 }}
                >
                    {loading ? <CircularProgress color="primary" size="1.5rem" /> : icon ? <Iconify icon={icon} /> : undefined}
                </div>
            )}
            <FormControl fullWidth>
                <TextField
                    id={id}
                    value={!checkUndefined(value) ? value : null}
                    label={!hideLabelEdit ? l : undefined}
                    onChange={handleChange}
                    onKeyUp={(e) => (!checkUndefinedOrNull(onKeyUp) && e.key === 'Enter' ? onKeyUp() : undefined)}
                    autoComplete={autocomplete ? 'section-ahdcars ' + autocomplete : 'off'}
                    error={(!loading && !checkUndefinedOrNull(error)) || !checkUndefinedOrNull(validate)}
                    helperText={!loading && !checkUndefinedOrNull(validate) ? validate : !loading && !checkUndefinedOrNull(error) ? error : helperText}
                    inputProps={{
                        placeholder: hideLabelEdit ? l : '',
                        min: min,
                        max: max,
                        step: !checkUndefinedOrNull(unrounded) ? 1 / Math.pow(10, unrounded) : 1,
                        style: { textTransform: upperCase ? 'uppercase' : undefined, textAlign: textAlign },
                    }}
                    disabled={loading || disabled}
                    InputProps={{
                        name: type === 'currency' ? ' €' : type === 'percent' ? ' %' : einheit ? ' ' + einheit : '',
                        inputComponent: NumberFormatCustom,
                    }}
                    InputLabelProps={{
                        shrink: !checkUndefinedNullOrEmpty(value),
                    }}
                />
            </FormControl>
            {!checkUndefinedOrNull(endIcon) && (
                <div
                    className="materialIcon"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: (!loading && !checkUndefinedOrNull(error)) || !checkUndefinedOrNull(validate) ? theme?.palette?.error?.main : undefined,
                        marginLeft: 8,
                        marginTop: 20,
                        marginBottom: -20,
                    }}
                >
                    <Iconify icon={endIcon} />
                </div>
            )}
            {!checkUndefinedOrNull(info) && (
                <div
                    className="materialIcon"
                    style={{
                        color: (!loading && !checkUndefinedOrNull(error)) || !checkUndefinedOrNull(validate) ? theme?.palette?.error?.main : undefined,
                        marginLeft: 8,
                        marginTop: 20,
                        marginBottom: -20,
                    }}
                >
                    <Tooltip title={info}>
                        <span>
                            <Info color="primary" />
                        </span>
                    </Tooltip>
                </div>
            )}
        </div>
    ) : (
        <InputNonEditValue
            autoWidth={autoWidth}
            endIcon={endIcon}
            fatLabel={fatLabel}
            finalId={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            hideLabel={hideLabel}
            icon={icon}
            info={info}
            l={l}
            loading={loading}
            textAlign={textAlign}
            value={convertNumberValue(v, type, unrounded, einheit, ca)}
        />
    );
};

export default InputNumber;
